import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Dialog } from '@/shared/components/ui/Dialog';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import type { RollupAggregate, RollupColumnType, RollupConfig } from '@/shared/formula/rollups';
import { computeRollup, resolveLookup, resolveLinkedTargetValues } from '@/shared/formula/rollups';
import type { GridFormatKey } from './formats';
import { useLinkableTables, type LinkableTable, type WorkbookSnapshot } from './linkedOptions';

const AGGREGATE_FUNCTIONS: readonly RollupAggregate[] = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX'];

const NUMBER_FORMATS: readonly { key: GridFormatKey | 'none'; labelKey: string }[] = [
  { key: 'none', labelKey: 'formatNone' },
  { key: 'currency', labelKey: 'formatCurrency' },
  { key: 'integer', labelKey: 'formatInteger' },
  { key: 'decimal', labelKey: 'formatDecimal' },
  { key: 'percent', labelKey: 'formatPercent' },
];

const MESSAGES: Record<'en' | 'es' | 'pt', Record<string, string>> = {
  en: {
    dialogTitle: 'Configure Rollup Column',
    dialogDesc: 'Calculate summary values across linked records without manual formulas.',
    typeRollup: 'Rollup (Aggregate math)',
    typeLookup: 'Lookup (Retrieve field)',
    sourceCol: 'Linked column in this sheet',
    sourceColPlaceholder: 'Select linked column…',
    targetTable: 'Target table / sheet',
    targetTablePlaceholder: 'Select target sheet…',
    targetCol: 'Field to summarize / look up',
    targetColPlaceholder: 'Select field…',
    targetMatchCol: 'Match target field (Foreign Key)',
    agg: 'Aggregate function',
    format: 'Number format',
    formatNone: 'Default / Auto',
    formatCurrency: 'Currency ($#,##0.00)',
    formatInteger: 'Integer (#,##0)',
    formatDecimal: 'Decimal (#,##0.00)',
    formatPercent: 'Percentage (0.0%)',
    preview: 'Calculation preview',
    previewNoData: 'Select source and target fields to preview calculation.',
    previewEmpty: 'No matching records found for preview.',
    previewResult: 'Sample result: {result}',
    cancel: 'Cancel',
    save: 'Apply Rollup',
    fnSum: 'SUM (Total)',
    fnAvg: 'AVG (Average)',
    fnCount: 'COUNT (Count records)',
    fnMin: 'MIN (Lowest)',
    fnMax: 'MAX (Highest)',
    separator: 'Separator for multiple values',
  },
  es: {
    dialogTitle: 'Configurar columna de resumen',
    dialogDesc: 'Calcula valores de resumen en registros vinculados sin fórmulas manuales.',
    typeRollup: 'Resumen (Cálculo agregado)',
    typeLookup: 'Búsqueda (Obtener campo)',
    sourceCol: 'Columna vinculada en esta hoja',
    sourceColPlaceholder: 'Seleccionar columna vinculada…',
    targetTable: 'Tabla / hoja de destino',
    targetTablePlaceholder: 'Seleccionar hoja de destino…',
    targetCol: 'Campo a resumir / consultar',
    targetColPlaceholder: 'Seleccionar campo…',
    targetMatchCol: 'Coincidir con campo destino',
    agg: 'Función de agregación',
    format: 'Formato numérico',
    formatNone: 'Predeterminado / Automático',
    formatCurrency: 'Moneda ($#,##0.00)',
    formatInteger: 'Entero (#,##0)',
    formatDecimal: 'Decimal (#,##0,00)',
    formatPercent: 'Porcentaje (0.0%)',
    preview: 'Vista previa del cálculo',
    previewNoData: 'Selecciona campos de origen y destino para ver la vista previa.',
    previewEmpty: 'No se encontraron registros coincidentes para la vista previa.',
    previewResult: 'Resultado de muestra: {result}',
    cancel: 'Cancelar',
    save: 'Aplicar resumen',
    fnSum: 'SUM (Suma total)',
    fnAvg: 'AVG (Promedio)',
    fnCount: 'COUNT (Contar registros)',
    fnMin: 'MIN (Mínimo)',
    fnMax: 'MAX (Máximo)',
    separator: 'Separador para múltiples valores',
  },
  pt: {
    dialogTitle: 'Configurar coluna de resumo',
    dialogDesc: 'Calcule valores de resumo em registros vinculados sem fórmulas manuais.',
    typeRollup: 'Resumo (Cálculo agregado)',
    typeLookup: 'Consulta (Obter campo)',
    sourceCol: 'Coluna vinculada nesta planilha',
    sourceColPlaceholder: 'Selecionar coluna vinculada…',
    targetTable: 'Tabela / planilha de destino',
    targetTablePlaceholder: 'Selecionar planilha de destino…',
    targetCol: 'Campo para resumir / consultar',
    targetColPlaceholder: 'Selecionar campo…',
    targetMatchCol: 'Corresponder com campo destino',
    agg: 'Função de agregação',
    format: 'Formato numérico',
    formatNone: 'Padrão / Automático',
    formatCurrency: 'Moeda ($#,##0.00)',
    formatInteger: 'Inteiro (#,##0)',
    formatDecimal: 'Decimal (#,##0,00)',
    formatPercent: 'Porcentagem (0.0%)',
    preview: 'Pré-visualização do cálculo',
    previewNoData: 'Selecione campos de origem e destino para pré-visualizar.',
    previewEmpty: 'Nenhum registro correspondente encontrado para pré-visualização.',
    previewResult: 'Resultado de amostra: {result}',
    cancel: 'Cancelar',
    save: 'Aplicar resumo',
    fnSum: 'SUM (Soma total)',
    fnAvg: 'AVG (Média)',
    fnCount: 'COUNT (Contar registros)',
    fnMin: 'MIN (Mínimo)',
    fnMax: 'MAX (Máximo)',
    separator: 'Separador para múltiplos valores',
  },
};

export interface RollupColumnDialogProps {
  /** Initial rollup config if already configured on this column */
  readonly initialConfig?: RollupConfig | undefined;
  /** Current column ID being configured */
  readonly columnField: string;
  /** Available columns in the current active sheet */
  readonly currentColumns: readonly { field: string; name: string }[];
  /** Current active sheet tab ID */
  readonly currentSheetId: string;
  /** Tenant organization ID */
  readonly orgId: string | null;
  /** Workbook snapshot with sheets and rows */
  readonly workbook?: WorkbookSnapshot | undefined;
  /** Column renames map */
  readonly renames?: Readonly<Record<string, Record<string, string>>> | undefined;
  /** Calculated columns map */
  readonly calculated?: Readonly<Record<string, readonly { field: string; name: string }[]>> | undefined;
  /** Persist callback saving configuration */
  readonly onPersist: (config: RollupConfig, formatKey?: GridFormatKey) => void;
  /** Close dialog callback */
  readonly onClose: () => void;
}

export function RollupColumnDialog({
  initialConfig,
  columnField,
  currentColumns,
  currentSheetId,
  orgId,
  workbook,
  renames = {},
  calculated = {},
  onPersist,
  onClose,
}: RollupColumnDialogProps) {
  const { locale } = useI18n();
  const dict = MESSAGES[locale as 'en' | 'es' | 'pt'] ?? MESSAGES.en;
  const msg = (key: string) => dict[key] ?? MESSAGES.en[key] ?? key;

  // Resolve linkable tables across workbook and organization
  const { tables, loading: tablesLoading } = useLinkableTables(true, orgId, workbook, renames, calculated);

  // Eligible source columns: exclude the column currently being edited
  const eligibleSourceColumns = useMemo(
    () => currentColumns.filter((c) => c.field !== columnField),
    [currentColumns, columnField],
  );

  // Default sourceCol: first eligible column or initialConfig
  const defaultSourceCol = initialConfig?.sourceCol ?? (eligibleSourceColumns[0]?.field ?? '');

  const [type, setType] = useState<RollupColumnType>(initialConfig?.type ?? 'rollup');
  const [sourceCol, setSourceCol] = useState<string>(defaultSourceCol);
  const [targetTab, setTargetTab] = useState<string>(initialConfig?.targetTab ?? '');
  const [targetMatchCol, setTargetMatchCol] = useState<string>(initialConfig?.targetMatchCol ?? 'id');
  const [targetCol, setTargetCol] = useState<string>(initialConfig?.targetCol ?? '');
  const [agg, setAgg] = useState<RollupAggregate>(initialConfig?.agg ?? 'SUM');
  const [formatKey, setFormatKey] = useState<GridFormatKey | 'none'>('none');
  const [separator, setSeparator] = useState<string>(initialConfig?.separator ?? ', ');

  // Selected target table metadata
  const selectedTable: LinkableTable | undefined = useMemo(() => {
    if (!targetTab) return tables[0];
    return tables.find((t) => t.tabId === targetTab);
  }, [tables, targetTab]);

  // When tables load, auto-select first target sheet if none set
  useEffect(() => {
    if (!targetTab && tables.length > 0) {
      // Prefer sibling sheet different from currentSheetId if available
      const sibling = tables.find((t) => t.tabId !== currentSheetId) ?? tables[0];
      if (sibling) {
        setTargetTab(sibling.tabId);
        if (sibling.columns.length > 0 && !targetCol) {
          setTargetCol(sibling.columns[0]?.field ?? '');
        }
      }
    }
  }, [tables, targetTab, currentSheetId, targetCol]);

  // Update targetCol when selectedTable changes if targetCol is not in table
  const availableTargetColumns = selectedTable?.columns ?? [];
  useEffect(() => {
    if (availableTargetColumns.length > 0) {
      const exists = availableTargetColumns.some((c) => c.field === targetCol);
      if (!exists && availableTargetColumns[0]) {
        setTargetCol(availableTargetColumns[0].field);
      }
    }
  }, [availableTargetColumns, targetCol]);

  // Live calculation preview
  const preview = useMemo(() => {
    if (!sourceCol || !targetTab || !targetCol || !workbook) return null;

    const currentSheet = workbook.sheets.find((s) => s.id === currentSheetId);
    const targetSheet = workbook.sheets.find((s) => s.id === targetTab);
    if (!currentSheet || !targetSheet || targetSheet.rows.length === 0) return null;

    // Find first row with a non-empty sourceCol
    const sampleRow = currentSheet.rows.find((r) => r[sourceCol] !== null && r[sourceCol] !== undefined && r[sourceCol] !== '');
    if (!sampleRow) return { empty: true };

    const config: RollupConfig = {
      type,
      sourceCol,
      targetTab,
      targetMatchCol: targetMatchCol || 'id',
      targetCol,
      agg,
      separator,
    };

    const targetValues = resolveLinkedTargetValues(sampleRow, config, targetSheet.rows);
    if (targetValues.length === 0) return { empty: true };

    const result = type === 'lookup' ? resolveLookup(targetValues, separator) : computeRollup(agg, targetValues);
    return {
      sampleSourceValue: sampleRow[sourceCol],
      count: targetValues.length,
      result,
    };
  }, [sourceCol, targetTab, targetCol, type, agg, separator, targetMatchCol, workbook, currentSheetId]);

  const isValid = Boolean(sourceCol && targetTab && targetCol);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!isValid) return;

    const config: RollupConfig = {
      type,
      sourceCol,
      targetTab,
      ...(targetMatchCol && targetMatchCol !== 'id' ? { targetMatchCol } : {}),
      targetCol,
      ...(type === 'rollup' ? { agg } : {}),
      ...(type === 'lookup' && separator !== ', ' ? { separator } : {}),
    };

    onPersist(config, formatKey === 'none' ? undefined : formatKey);
    onClose();
  };

  return (
    <Dialog
      title={msg('dialogTitle')}
      description={msg('dialogDesc')}
      onClose={onClose}
      footer={
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-focus"
          >
            {msg('cancel')}
          </button>
          <button
            type="button"
            disabled={!isValid}
            onClick={handleSubmit}
            className="min-h-[44px] rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-opacity hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-focus disabled:opacity-50"
          >
            {msg('save')}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 py-1 text-sm text-text">
        {/* Mode Toggle: Rollup vs Lookup */}
        <div className="flex rounded-lg border border-border bg-surface-2 p-1">
          <button
            type="button"
            onClick={() => setType('rollup')}
            className={`flex-1 min-h-[36px] rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              type === 'rollup'
                ? 'bg-surface text-text shadow-sm'
                : 'text-muted hover:text-text'
            }`}
          >
            {msg('typeRollup')}
          </button>
          <button
            type="button"
            onClick={() => setType('lookup')}
            className={`flex-1 min-h-[36px] rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              type === 'lookup'
                ? 'bg-surface text-text shadow-sm'
                : 'text-muted hover:text-text'
            }`}
          >
            {msg('typeLookup')}
          </button>
        </div>

        {/* Source Linked Column */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rollup-source-col" className="text-xs font-semibold text-text">
            {msg('sourceCol')}
          </label>
          <select
            id="rollup-source-col"
            value={sourceCol}
            onChange={(e) => setSourceCol(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors focus:border-focus focus:ring-1 focus:ring-focus"
          >
            {eligibleSourceColumns.length === 0 ? (
              <option value="">{msg('sourceColPlaceholder')}</option>
            ) : (
              eligibleSourceColumns.map((col) => (
                <option key={col.field} value={col.field}>
                  {col.name || col.field}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Target Sheet & Field */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="rollup-target-tab" className="text-xs font-semibold text-text">
              {msg('targetTable')}
            </label>
            <select
              id="rollup-target-tab"
              value={targetTab}
              disabled={tablesLoading}
              onChange={(e) => {
                setTargetTab(e.target.value);
                const nextTable = tables.find((t) => t.tabId === e.target.value);
                if (nextTable && nextTable.columns[0]) {
                  setTargetCol(nextTable.columns[0].field);
                }
              }}
              className="min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors focus:border-focus focus:ring-1 focus:ring-focus disabled:opacity-50"
            >
              {tables.length === 0 ? (
                <option value="">{msg('targetTablePlaceholder')}</option>
              ) : (
                tables.map((t) => (
                  <option key={t.tabId} value={t.tabId}>
                    {t.documentName ? `${t.documentName} / ${t.tabName}` : t.tabName}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="rollup-target-col" className="text-xs font-semibold text-text">
              {msg('targetCol')}
            </label>
            <select
              id="rollup-target-col"
              value={targetCol}
              onChange={(e) => setTargetCol(e.target.value)}
              className="min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors focus:border-focus focus:ring-1 focus:ring-focus"
            >
              {availableTargetColumns.length === 0 ? (
                <option value="">{msg('targetColPlaceholder')}</option>
              ) : (
                availableTargetColumns.map((col) => (
                  <option key={col.field} value={col.field}>
                    {col.name || col.field}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Target Match Column (Foreign Key) */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rollup-match-col" className="text-xs font-semibold text-muted">
            {msg('targetMatchCol')}
          </label>
          <select
            id="rollup-match-col"
            value={targetMatchCol}
            onChange={(e) => setTargetMatchCol(e.target.value)}
            className="min-h-[40px] w-full rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-xs text-text outline-none transition-colors focus:border-focus"
          >
            <option value="id">id (Primary record ID)</option>
            {availableTargetColumns.map((col) => (
              <option key={`match-${col.field}`} value={col.field}>
                {col.name || col.field}
              </option>
            ))}
          </select>
        </div>

        {/* Aggregate Function (Only for type: 'rollup') */}
        {type === 'rollup' && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-text">{msg('agg')}</label>
            <div className="grid grid-cols-5 gap-1.5">
              {AGGREGATE_FUNCTIONS.map((fn) => {
                const isSelected = agg === fn;
                return (
                  <button
                    key={fn}
                    type="button"
                    onClick={() => setAgg(fn)}
                    className={`min-h-[44px] rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors ${
                      isSelected
                        ? 'border-accent bg-accent/10 text-accent font-bold ring-1 ring-accent'
                        : 'border-border bg-surface text-text hover:bg-surface-2'
                    }`}
                  >
                    {fn}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Separator (Only for type: 'lookup') */}
        {type === 'lookup' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="lookup-separator" className="text-xs font-semibold text-text">
              {msg('separator')}
            </label>
            <input
              id="lookup-separator"
              type="text"
              value={separator}
              onChange={(e) => setSeparator(e.target.value)}
              className="min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors focus:border-focus"
              placeholder=", "
            />
          </div>
        )}

        {/* Number Formatting */}
        {type === 'rollup' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="rollup-format" className="text-xs font-semibold text-text">
              {msg('format')}
            </label>
            <select
              id="rollup-format"
              value={formatKey}
              onChange={(e) => setFormatKey(e.target.value as GridFormatKey | 'none')}
              className="min-h-[44px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors focus:border-focus"
            >
              {NUMBER_FORMATS.map((fmt) => (
                <option key={fmt.key} value={fmt.key}>
                  {msg(fmt.labelKey)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Live Calculation Preview Card */}
        <div className="mt-1 rounded-lg border border-border bg-surface-2 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted">
            <Icon name="sheets" className="h-3.5 w-3.5" />
            <span>{msg('preview')}</span>
          </div>
          <div className="mt-2 text-xs">
            {!preview ? (
              <span className="text-muted">{msg('previewNoData')}</span>
            ) : preview.empty ? (
              <span className="text-muted">{msg('previewEmpty')}</span>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="text-muted">
                  Sample key: <span className="font-mono text-text">{String(preview.sampleSourceValue)}</span> ({preview.count} matched records)
                </div>
                <div className="font-medium text-text">
                  {msg('previewResult').replace('{result}', String(preview.result ?? 'null'))}
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </Dialog>
  );
}
